import { useEffect, useRef, useState, type ImgHTMLAttributes } from 'react';
import { WifiOff } from 'lucide-react';

/**
 * Reemplaza `<img>` con una versión que:
 *   1. Reintenta cargar la imagen automáticamente cuando el
 *      navegador reporta `online` después de haber estado offline
 *      (escucha `iftmx:reconnected` y `online`).
 *   2. Muestra un indicador discreto "sin conexión" cuando la imagen
 *      falla y estamos offline, para que el usuario sepa que la
 *      imagen NO se cargó por falta de red (no porque no exista).
 *
 * Casos de uso típicos:
 *   - Foto de perfil del usuario
 *   - Thumbnails de YouTube
 *   - Fotos de evidencia (cardio, InBody, comidas)
 *   - Imágenes de demostración de ejercicios
 *
 * Si la imagen carga OK desde el principio, este componente se
 * comporta exactamente igual que un `<img>` normal — el reintento
 * solo se activa si hubo error.
 *
 * Si pasas un `fallback`, eso se renderiza en lugar de la imagen
 * cuando hay error (típicamente iniciales del usuario, ícono, etc).
 */
interface ResilientImageProps extends Omit<ImgHTMLAttributes<HTMLImageElement>, 'onError' | 'onLoad'> {
  /** ReactNode a mostrar cuando la imagen falla. Si no se pasa,
   *  muestra simplemente el `<img>` roto del navegador. */
  fallback?: React.ReactNode;
  /** Si true, agrega un overlay con ícono de "sin conexión" sobre
   *  el fallback cuando estamos offline Y la imagen falló. Default true. */
  showOfflineHint?: boolean;
  /** Callback opcional cuando la imagen carga exitosamente. */
  onLoaded?: () => void;
  /** Override del timeout. Si no se pasa, se usa un timeout
   *  ADAPTATIVO según el estado de conexión. */
  timeoutMs?: number;
  /** URLs alternas a probar en orden si `src` falla. Útil para
   *  thumbnails de YouTube donde maxresdefault no existe en todos
   *  los videos y hay que caer a hqdefault → mqdefault → default. */
  fallbackSrcs?: string[];
}

const TIMEOUT_OFFLINE_MS = 5_000;
const TIMEOUT_ONLINE_MS = 30_000;

export function ResilientImage({
  src,
  alt,
  fallback,
  showOfflineHint = true,
  onLoaded,
  className,
  timeoutMs,
  fallbackSrcs,
  ...rest
}: ResilientImageProps) {
  // Versión interna: cada vez que reintentamos, bumpeamos el version
  // para forzar que React re-renderice el <img> (browsers cachean por
  // src — para forzar refetch usamos un query param ?v=N).
  const [version, setVersion] = useState(0);
  const [errored, setErrored] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [online, setOnline] = useState(typeof navigator !== 'undefined' ? navigator.onLine : true);
  // Índice del fallback que estamos probando. Empezamos en -1 (= usar
  // src original); al fallar avanzamos por fallbackSrcs[0], [1]...
  const [fallbackIdx, setFallbackIdx] = useState(-1);
  const wasOfflineRef = useRef(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // src actual: si fallbackIdx === -1 usamos src; si no, fallbackSrcs[fallbackIdx]
  const currentSrc = fallbackIdx >= 0 && fallbackSrcs && fallbackSrcs[fallbackIdx]
    ? fallbackSrcs[fallbackIdx]
    : src;

  // Timeout adaptativo: si después de N ms la imagen no cargó NI
  // falló, forzamos el estado errored. iOS PWA en airplane mode a
  // veces deja requests colgadas sin disparar onerror, así que el
  // usuario se quedaba viendo un placeholder gris para siempre.
  //
  // Adaptativo según conexión:
  //   - offline → 5s (queremos fallback inmediato; sabemos que no
  //     va a cargar)
  //   - online → 30s (generoso; cellular lento puede tardar bastante
  //     legítimamente; mejor esperar que matar la carga)
  //
  // Bug previo (fijo en 5s): cargas legítimas en red lenta se
  // marcaban como errored y la foto nunca aparecía al reconectar.
  useEffect(() => {
    if (loaded || errored || !src) return;
    const effectiveTimeout = timeoutMs ?? (online ? TIMEOUT_ONLINE_MS : TIMEOUT_OFFLINE_MS);
    timeoutRef.current = setTimeout(() => {
      setErrored(true);
    }, effectiveTimeout);
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [src, version, loaded, errored, timeoutMs, online, fallbackIdx]);

  // Retry on visibility / focus / periodic — sin esto, una falla
  // transitoria (red lenta, switch de WiFi a celular, etc.) dejaba
  // la imagen muerta hasta que el usuario cerrara y volviera a abrir
  // la app. Ahora reintentamos cuando:
  //   1. El usuario vuelve a la tab/PWA (visibilitychange visible)
  //   2. La ventana recibe foco (focus)
  //   3. Pasaron 30s con la imagen en error y la página está activa
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!errored) return;

    const retry = () => {
      if (document.visibilityState !== 'visible') return;
      setErrored(false);
      setLoaded(false);
      setFallbackIdx(-1);  // volver al src original al reintentar manual
      setVersion(v => v + 1);
    };

    const onVisible = () => { if (document.visibilityState === 'visible') retry(); };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', retry);

    // Reintento periódico cada 30s mientras la página esté visible
    // y la imagen siga en error. Suficientemente espaciado para no
    // martillar la red, suficientemente frecuente para que el usuario
    // no perciba que la imagen "se quedó atorada".
    const periodicInterval = setInterval(retry, 30_000);

    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', retry);
      clearInterval(periodicInterval);
    };
  }, [errored]);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleOnline = () => {
      setOnline(true);
      // Solo reintentamos si la imagen falló previamente. Si cargó
      // bien la primera vez no hay nada que reintentar.
      if (errored) {
        setErrored(false);
        setLoaded(false);
        setVersion(v => v + 1);
      }
    };
    const handleOffline = () => {
      setOnline(false);
      wasOfflineRef.current = true;
    };

    // Evento custom global de la app que dispara cuando se restaura
    // conexión (ver useOnlineStatus). Es más confiable que `online`
    // porque algunos navegadores (iOS Safari) no siempre disparan
    // el evento nativo cuando vuelve la red mid-session.
    const handleReconnected = () => {
      setOnline(true);
      if (errored) {
        setErrored(false);
        setLoaded(false);
        setVersion(v => v + 1);
      }
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    window.addEventListener('iftmx:reconnected', handleReconnected);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('iftmx:reconnected', handleReconnected);
    };
  }, [errored]);

  // Construir el src real con cache-busting en reintentos para que
  // el navegador no devuelva el 404/error cacheado de la primera
  // intentona.
  const effectiveSrc = currentSrc && version > 0
    ? `${currentSrc}${currentSrc.includes('?') ? '&' : '?'}_retry=${version}`
    : currentSrc;

  if (errored && fallback !== undefined) {
    return (
      // Wrapper `relative` para anclar el indicador absoluto; `block`
      // por defecto para que w-full/h-full del parent funcionen igual
      // que con un `<img>` (que también es block-level con esas
      // clases). Usar `inline-block` rompía el layout en algunos
      // contenedores tipo card.
      <div className={`relative ${className || ''}`}>
        {fallback}
        {showOfflineHint && !online && (
          <span
            className="absolute bottom-1 right-1 bg-amber-500 text-amber-950 rounded-full p-1 shadow-lg ring-2 ring-amber-700/30"
            title="Sin conexión — esta imagen se cargará al reconectar"
            aria-label="Imagen no disponible offline"
          >
            <WifiOff className="w-3 h-3" />
          </span>
        )}
      </div>
    );
  }

  return (
    <img
      key={`${fallbackIdx}-${version}`}
      src={effectiveSrc}
      alt={alt}
      className={className}
      onError={() => {
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        // Si hay fallbackSrcs, avanzar al siguiente en cascada antes
        // de marcar como errored definitivo. Útil para thumbnails
        // de YouTube: maxres → hq → mq → sd → default.
        const nextIdx = fallbackIdx + 1;
        if (fallbackSrcs && nextIdx < fallbackSrcs.length) {
          setFallbackIdx(nextIdx);
          setLoaded(false);
        } else {
          setErrored(true);
        }
      }}
      onLoad={() => {
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        setLoaded(true);
        if (errored) setErrored(false);
        onLoaded?.();
      }}
      {...rest}
    />
  );
}
